import { createContext, FC, ReactNode, useContext } from 'react';
import { getBorderColor } from '@billing/branding';

// The merchant brand-aware border/divider colour, derived once from the
// generated `semanticColors` via `@billing/branding`'s `getBorderColor`
// (PAA-2548).
//
// `getBorderColor` reads `semanticColors.background.primary` /
// `semanticColors.text.primary` and is brand-aware (it returns the neutral
// decorative grey when the background is white/unset — including a near-white
// background the theme generator normalizes to white — rather than a
// translucent merchant text colour). We compute it once at
// `ThemedBrandingShell`, right after the LHS semantic palette is generated, and
// expose it here for every border/divider to consume. Mirrors the
// digital-invoice app's `useBorderColor` (billing-payer-experiences).
//
// The default is `getBorderColor()` (no seeds → the neutral grey), so consumers
// rendered outside a `ThemedBrandingShell` (e.g. un-themed pages) still get a
// sensible border colour instead of throwing.
const BorderColorContext = createContext<string>(getBorderColor());

export const BorderColorProvider: FC<{ borderColor: string; children: ReactNode }> = ({ borderColor, children }) => (
  <BorderColorContext.Provider value={borderColor}>{children}</BorderColorContext.Provider>
);

// The merchant brand-aware border/divider colour.
export const useBorderColor = (): string => useContext(BorderColorContext);
